import React, { useMemo, useState } from "react";
import { Button, Empty, Input, List, Modal, Tag, Typography } from "antd";
import { connect } from "react-redux";
import { fetchUserCreatedVideos } from "../../actions/contentCreation";
import VideoThumbnailCard, { VideoThumbnailCardSkeleton } from "../VideoThumbnailCard/VideoThumbnailCard";
import "./MyVideoSelector.less";

const { Text } = Typography;

const normalizeVideo = (video) => ({
    ...video,
    title: video?.title || "Untitled video",
    videoType: video?.videoType || video?.video_type || "youtube",
    embedId: video?.embedId || video?.embed_id || "",
    duration: video?.duration || 0
});

const MyVideoSelector = ({
    user,
    userCreatedVideos,
    receivedUserCreatedVideos,
    fetchingUserCreatedVideos,
    fetchUserCreatedVideos,
    existingVideoIds = [],
    addVideosToVideoBank
}) => {
    const [visible, setVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const videoSkeletonList = useMemo(() => Array.from({ length: 8 }, (_, idx) => ({ id: `skeleton-${idx}` })), []);

    const excludedIds = useMemo(() => new Set(existingVideoIds.filter(Boolean)), [existingVideoIds]);

    const availableVideos = useMemo(() => {
        return (userCreatedVideos || [])
            .map(normalizeVideo)
            .filter((video) => video.embedId && !excludedIds.has(video.embedId));
    }, [userCreatedVideos, excludedIds]);

    const filteredVideos = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        if (!query) {
            return availableVideos;
        }
        return availableVideos.filter((video) => {
            const title = (video?.title || "").toLowerCase();
            const embedId = (video?.embedId || "").toLowerCase();
            return title.includes(query) || embedId.includes(query);
        });
    }, [availableVideos, searchTerm]);

    const selectedVideos = useMemo(() => {
        const selectedSet = new Set(selectedIds);
        return availableVideos.filter((video) => selectedSet.has(video.embedId));
    }, [availableVideos, selectedIds]);

    const handleOpen = async () => {
        setVisible(true);
        if (!receivedUserCreatedVideos && user?.id) {
            await fetchUserCreatedVideos(user.id);
        }
    };

    const handleToggleSelect = (embedId) => {
        setSelectedIds((prev) => (
            prev.includes(embedId)
                ? prev.filter((id) => id !== embedId)
                : [...prev, embedId]
        ));
    };

    const handleImport = () => {
        if (!selectedVideos.length) {
            return;
        }
        addVideosToVideoBank(selectedVideos);
        setVisible(false);
        setSearchTerm("");
        setSelectedIds([]);
    };

    return (
        <>
            <Button type="link" className="my-video-selector__trigger" onClick={handleOpen}>
                My Videos
            </Button>
            <Modal
                visible={visible}
                title="My Videos"
                width={1024}
                style={{ padding: "12px" }}
                onCancel={() => {
                    setVisible(false);
                    setSearchTerm("");
                    setSelectedIds([]);
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setVisible(false);
                            setSearchTerm("");
                            setSelectedIds([]);
                        }}>
                        Cancel
                    </Button>,
                    <Button
                        key="import"
                        type="primary"
                        disabled={!selectedVideos.length}
                        onClick={handleImport}>
                        Import Videos ({selectedVideos.length})
                    </Button>
                ]}>
                <div style={{ padding: "8px 16px" }}>
                    <div className="my-video-selector__toolbar">
                        <Input.Search
                            allowClear
                            placeholder="Search videos by title or embed ID"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            className="my-video-selector__search"
                        />
                        <Tag color={selectedVideos.length > 0 ? "blue" : "default"} className="my-video-selector__selected-tag">
                            Selected: {selectedVideos.length}
                        </Tag>
                    </div>
                    <Text type="secondary" className="my-video-selector__help-text">
                        Select your videos and click Import Videos to add them to video bank.
                    </Text>
                </div>

                <div style={{ padding: "8px 16px" }}>
                    <List
                        className="my-content-video-list my-video-selector__list"
                        grid={{
                            gutter: [4, 4],
                            xs: 1,
                            sm: 2,
                            md: 4,
                            lg: 4,
                            xl: 4,
                            xxl: 4
                        }}
                        dataSource={fetchingUserCreatedVideos && !receivedUserCreatedVideos ? videoSkeletonList : filteredVideos}
                        pagination={{
                            pageSize: 8
                        }}
                        emptyText={null}
                        renderItem={(video, index) => {
                            const isLoading = fetchingUserCreatedVideos && !receivedUserCreatedVideos;
                            if (isLoading) {
                                return (
                                    <List.Item key={video.id || index}>
                                        <VideoThumbnailCardSkeleton />
                                    </List.Item>
                                );
                            }

                            const isSelected = selectedIds.includes(video.embedId);
                            return (
                                <List.Item key={video.id || video.embedId || video.title || index}>
                                    <button
                                        type="button"
                                        className={`my-video-selector__card-btn ${isSelected ? "is-selected" : ""}`}
                                        onClick={() => handleToggleSelect(video.embedId)}>
                                        <VideoThumbnailCard
                                            index={index}
                                            video={video}
                                            hoverable={false}
                                            showTopOverlay={true}
                                            searchTerm={searchTerm}
                                            showMenu={false}
                                        />
                                    </button>
                                </List.Item>
                            );
                        }}
                    />
                </div>

            </Modal>
        </>
    );
};

const mapStateToProps = (state) => ({
    user: state.userReducer?.user,
    userCreatedVideos: state.contentCreationReducer.userCreatedVideos,
    receivedUserCreatedVideos: state.contentCreationReducer.receivedUserCreatedVideos,
    fetchingUserCreatedVideos: state.contentCreationReducer.fetchingUserCreatedVideos
});

export default connect(mapStateToProps, { fetchUserCreatedVideos })(MyVideoSelector);
